import React from 'react';
import {
  IconBrandFacebook,
  IconBrandTwitter,
  IconBrandLinkedin,
  IconSearch,
} from '@tabler/icons-react';
import { cn } from '@/admin/lib/utils';

export type SocialPlatform = 'facebook' | 'twitter' | 'linkedin' | 'google';

interface SocialPreviewCardProps {
  platform: SocialPlatform;
  title: string;
  description: string;
  imageUrl?: string;
  url?: string;
  siteName?: string;
}

const platformConfig: Record<
  SocialPlatform,
  {
    name: string;
    icon: React.FC<{ className?: string }>;
    bgColor: string;
    iconBg: string;
  }
> = {
  facebook: {
    name: 'Facebook',
    icon: IconBrandFacebook,
    bgColor: 'bg-[#f0f2f5]',
    iconBg: 'bg-[#1877f2] text-white',
  },
  twitter: {
    name: 'Twitter / X',
    icon: IconBrandTwitter,
    bgColor: 'bg-black',
    iconBg: 'bg-black text-white',
  },
  linkedin: {
    name: 'LinkedIn',
    icon: IconBrandLinkedin,
    bgColor: 'bg-white',
    iconBg: 'bg-[#0a66c2] text-white',
  },
  google: {
    name: 'Google Search',
    icon: IconSearch,
    bgColor: 'bg-white',
    iconBg: 'bg-[#4285f4] text-white',
  },
};

export const SocialPreviewCard: React.FC<SocialPreviewCardProps> = ({
  platform,
  title,
  description,
  imageUrl,
  url,
  siteName,
}) => {
  const config = platformConfig[platform];
  const Icon = config.icon;
  const domain = url ? new URL(url).hostname : siteName || 'cueboard.ai';

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 3) + '...';
  };

  if (platform === 'google') {
    return (
      <div className="rounded-lg border overflow-hidden bg-white">
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
          <div className={cn('p-1 rounded', config.iconBg)}>
            <Icon className="h-3 w-3" />
          </div>
          <span className="text-xs font-medium">{config.name}</span>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
              C
            </div>
            <div>
              <p className="text-xs text-foreground">{siteName || 'Cueboard'}</p>
              <p className="text-[10px] text-muted-foreground">{domain}</p>
            </div>
          </div>
          <h3 className="text-[#1a0dab] text-base hover:underline cursor-pointer leading-tight">
            {truncate(title || 'Page Title', 60)}
          </h3>
          <p className="text-xs text-[#545454] mt-1 leading-relaxed">
            {truncate(description || 'Page description will appear here...', 160)}
          </p>
        </div>
      </div>
    );
  }

  if (platform === 'twitter') {
    return (
      <div className="rounded-lg border overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
          <div className={cn('p-1 rounded', config.iconBg)}>
            <Icon className="h-3 w-3" />
          </div>
          <span className="text-xs font-medium">{config.name}</span>
        </div>
        <div className={cn('relative', config.bgColor)}>
          {imageUrl ? (
            <div className="relative">
              <img
                src={imageUrl}
                alt="Preview"
                className="w-full aspect-[1.91/1] object-cover"
                onError={e => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded">
                {domain}
              </div>
            </div>
          ) : (
            <div className="aspect-[1.91/1] bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-sm">No image</span>
            </div>
          )}
          <div className="p-3 bg-[#16181c] text-white">
            <h3 className="text-sm font-normal leading-tight truncate">
              {title || 'Page Title'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
              {truncate(description || 'Description...', 100)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (platform === 'linkedin') {
    return (
      <div className="rounded-lg border overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
          <div className={cn('p-1 rounded', config.iconBg)}>
            <Icon className="h-3 w-3" />
          </div>
          <span className="text-xs font-medium">{config.name}</span>
        </div>
        <div className={cn(config.bgColor)}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Preview"
              className="w-full aspect-[1.91/1] object-cover"
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="aspect-[1.91/1] bg-muted flex items-center justify-center">
              <span className="text-muted-foreground text-sm">No image</span>
            </div>
          )}
          <div className="p-3 border-t bg-[#f3f2f0]">
            <h3 className="text-sm font-semibold text-black leading-tight truncate">
              {title || 'Page Title'}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">{domain}</p>
          </div>
        </div>
      </div>
    );
  }

  // Facebook
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        <div className={cn('p-1 rounded', config.iconBg)}>
          <Icon className="h-3 w-3" />
        </div>
        <span className="text-xs font-medium">{config.name}</span>
      </div>
      <div className={cn(config.bgColor)}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Preview"
            className="w-full aspect-[1.91/1] object-cover"
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="aspect-[1.91/1] bg-muted flex items-center justify-center">
            <span className="text-muted-foreground text-sm">No image</span>
          </div>
        )}
        <div className="p-3 border-t">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
            {domain}
          </p>
          <h3 className="text-sm font-semibold text-[#1c1e21] leading-tight truncate mt-0.5">
            {title || 'Page Title'}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            {truncate(description || 'Description...', 100)}
          </p>
        </div>
      </div>
    </div>
  );
};

interface SocialPreviewGridProps {
  title: string;
  description: string;
  imageUrl?: string;
  url?: string;
  siteName?: string;
  platforms?: SocialPlatform[];
}

export const SocialPreviewGrid: React.FC<SocialPreviewGridProps> = ({
  title,
  description,
  imageUrl,
  url,
  siteName = 'Cueboard',
  platforms = ['facebook', 'twitter', 'linkedin', 'google'],
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {platforms.map(platform => (
        <SocialPreviewCard
          key={platform}
          platform={platform}
          title={title}
          description={description}
          imageUrl={imageUrl}
          url={url}
          siteName={siteName}
        />
      ))}
    </div>
  );
};

